import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import {
  createKind,
  fetchKinds,
  fetchKind,
  reorderKinds,
  updateKind
} from "../../actions/community/kindsActions";

export interface KindInterface {
  url: string;

  id: number;
  app_id: number;
  target: 'content' | 'community' | 'assets';
  name: string;
  slug: string;

  thumbnail_url: string;
  posts_count: number;
  assets_count: number;

  order: number;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  newParams: KindInterface | undefined;
  newError: string | null;

  reorderIds: number[] | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null,

  reorderIds: null,
};

const adaptor = createEntityAdapter<KindInterface>();

const slice = createSlice({
  name: 'kinds',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    },
    setReorderIds: (state, action) => {
      state.reorderIds = action.payload.ids;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchKinds.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchKinds.fulfilled, (state, action) => {
        state.ids = action.payload.ids;
        state.entities = action.payload.kinds;
        state.count = action.payload.count;

        state.status = 'succeeded';
      })
      .addCase(fetchKinds.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })
      // #show
      .addCase(fetchKind.fulfilled, (state, action) => {
        const kind = Object.values(action.payload.kind)[0];
        state.entities[kind.id] = kind;
      })
      // #create
      .addCase(createKind.fulfilled, (state, action) => {
        const kind = Object.values(action.payload.kind)[0];
        state.entities[kind.id] = kind;
        state.ids.push(kind.id);
      })
      .addCase(createKind.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updateKind.fulfilled, (state, action) => {
        const kind = Object.values(action.payload.kind)[0];
        state.entities[kind.id] = kind;
      })
      .addCase(updateKind.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #reorder
      .addCase(reorderKinds.fulfilled, (state, action) => {
        const { ids } = action.payload;
        state.ids = ids;
      })
  }
});


const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setNewParams,
  setNewError,
  setEditError,
  setReorderIds
} = slice.actions;

export const {
  selectIds: selectKindIds,
  selectEntities: selectKindEntities,
  selectById: selectKindById
} = adaptor.getSelectors<RootState>(state => state.community.kinds);
